// Map #4 — Verdant Alien World (sim half). A turquoise-sky world under two suns: blue-green meadow with violet-tipped
// grass, smooth domed hills with luminous pools in the hollows, parasol and orb-crowned alien trees, spore-pod thickets,
// mossy ruin blocks and great violet crystal clusters that shatter into glowing shards.
// Archetype reskins (render.ts): spire = crystal cluster on a mossy plinth, boulder = mossy ruin block, rock = crystal
// shard cluster, tree = alien flora (5 variants below), bush = spore pods / glow-caps / fronds (3 variants).
// Hazards (light, telegraphed, hit hero AND foes):
//   SPORE BLOOMS   — mother pods inside the pod thickets swell (2.6 s warning) then burst into a spore cloud for 5 s:
//                    fog (visibility -45 %, everyone inside ~5 % slower) with a denser core that slows to 80 %.
//   RESONANCE GEODES — ground geodes hum and glow brighter (2.4 s warning), then discharge for 1.4 s: 5 HP/s to the
//                    hero (≈7 HP a pulse), 7.5 % max HP/s to foes (bosses a quarter). Repeat every ~17–23 s.
// PURE (check:ip enforces it): no three, no DOM, no unseeded randomness. Everything random comes from ctx.rng.
import type { MapCoreDef, HillSpec, TerrainSpec } from '../../core/mapdef';
import type { HazardDef } from '../../core/hazards';
import type { VariantDef } from '../../core/terrain';

/** alien trees (index = render flora variant) — r = trunk / stalk base radius at s = 1 */
export const ALIEN_TREES: VariantDef[] = [
  { name: 'parasol', r: 0.6, s: [0.95, 1.3], h: [0.95, 1.12], hp: 1.1, w: 3 },   // curved trunk, wide flat layered canopy
  { name: 'orbcrown', r: 0.75, s: [0.85, 1.15], h: [0.9, 1.05], hp: 1.15, w: 3 }, // bulb trunk, one big spherical crown
  { name: 'twinbulb', r: 0.62, s: [0.9, 1.2], h: [0.95, 1.1], hp: 1, w: 2 },     // forked trunk, two orb crowns
  { name: 'lantern', r: 0.42, s: [0.85, 1.1], h: [0.95, 1.15], hp: 0.6, w: 2 },  // slim arching stalk, hanging glow pods
  { name: 'totem', r: 0.58, s: [0.85, 1.15], h: [0.95, 1.12], hp: 1, w: 2 },     // stacked diminishing orbs
];
/** spore pods (bush archetype) — r = the pod mass radius at s = 1 (render scales the shape by BUSH_K like map #1) */
export const ALIEN_PODS: VariantDef[] = [
  { name: 'podcluster', r: 1.1, s: [0.8, 1.2], h: [0.9, 1.15], hp: 1, w: 3 },   // bulbous spore sacs with glowing tips
  { name: 'glowcaps', r: 1.0, s: [0.8, 1.15], h: [0.9, 1.15], hp: 0.8, w: 2 },  // a ring of glowing mushroom caps
  { name: 'frond', r: 1.3, s: [0.85, 1.15], h: [0.9, 1.1], hp: 1.1, w: 2 },     // magenta fan fronds
];

/** smooth domed hills; the shallow hollows hold luminous pools (render paints them from the hill height) */
export const VERDANT_HILLS: HillSpec = { mounds: 16, dip: 0.3, rad: [13, 24], dipRad: [9, 15], amp: [1.3, 2.7], dipAmp: [0.7, 1.15], swell: 0.35, max: 3.0, min: -1.2 };
export const VERDANT_TERRAIN: TerrainSpec = {
  hills: VERDANT_HILLS,
  props: [
    { kind: 'spire', count: 8, rMin: 26, inset: 7, size: [3.2, 4.8], clear: 12, h: [10, 16], hpPer: 4.5 },  // crystal clusters (19 at 110 m)
    { kind: 'boulder', count: 22, rMin: 12, inset: 3, size: [1.5, 2.5], clear: 4, h: [0.8, 1.25], hpPer: 2 }, // mossy ruin blocks
    { kind: 'tree', count: 64, rMin: 12, inset: 2, size: [0.7, 0.7], clear: 1.8, h: [1, 1], variants: ALIEN_TREES },
    { kind: 'rock', count: 46, rMin: 8, inset: 2, size: [0.5, 0.9], clear: 1, h: [0.8, 1.25] },              // crystal shards
    { kind: 'bush', count: 64, rMin: 9, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: ALIEN_PODS, layout: 'clumps' },
  ],
};

/** hazard tuning (tests read these) */
export const SPORE = { vents: 6, r: 6.5, core: 5, warn: 2.6, active: 5, fog: 0.45, slow: 0.8, every: [36, 48] as [number, number], start: 50 };
export const RESONANCE = { geodes: 5, r: 4.2, warn: 2.4, active: 1.4, power: 5, every: [17, 23] as [number, number], start: 25 };

/** vent / geode counts were authored on the 110 m arena; they scale with the arena area (owner 2026-11-02: 160 m) */
export const verdantCounts = (arenaR: number): { vents: number; geodes: number } => {
  const k = Math.max(1, (arenaR / 110) ** 2);
  return { vents: Math.round(SPORE.vents * k), geodes: Math.round(RESONANCE.geodes * k) };
};

type P = { x: number; z: number };
const farFrom = (x: number, z: number, list: readonly P[], d: number) => list.every((p) => (p.x - x) ** 2 + (p.z - z) ** 2 >= d * d);

export const MAP: MapCoreDef = {
  id: 'verdant-alien',
  name: 'Verdant Alien World',
  tagline: 'Two suns over a turquoise sky. Crystal clusters hum, spore pods bloom and the pools glow after dark.',
  order: 4,
  status: 'ready',
  time: { default: 'day', day: 'noon' },
  unlock: { after: 'rust-canyon', seconds: 480 },
  terrain: VERDANT_TERRAIN,
  fire: true,
  nightPressure: 1.15,
  boss: { name: 'THRUMBACK BEHEMOTH', hpMul: 1.15 },
  hazards: ({ rng, arenaR, props }) => {
    const out: HazardDef[] = [], C = verdantCounts(arenaR);
    const solids: (P & { r: number })[] = props.filter((p) => p.solid).map((p) => ({ x: p.x, z: p.z, r: p.r }));
    const clearOfSolids = (x: number, z: number, pad: number) => solids.every((s) => (s.x - x) ** 2 + (s.z - z) ** 2 >= (s.r + pad) ** 2);
    const polar = (r0: number, r1: number): P => { const a = rng.range(0, Math.PI * 2), d = Math.sqrt(rng.range((r0 / arenaR) ** 2, (r1 / arenaR) ** 2)) * arenaR; return { x: Math.cos(a) * d, z: Math.sin(a) * d }; };

    // ---- spore blooms: mother pods sit inside the pod thickets (bush clumps), away from the spawn and each other
    const trees: P[] = props.filter((p) => p.kind === 'tree').map((p) => ({ x: p.x, z: p.z }));
    const pods = props.filter((p) => p.kind === 'bush' && Math.hypot(p.x, p.z) >= 22 && Math.hypot(p.x, p.z) <= arenaR - 12);
    const vents: P[] = [];
    for (let k = 0; k < 240 && vents.length < C.vents && pods.length; k++) {
      const b = pods[Math.floor(rng.next() * pods.length) % pods.length];
      if (farFrom(b.x, b.z, vents, 30) && clearOfSolids(b.x, b.z, 2) && (k > 160 || farFrom(b.x, b.z, trees, 4.5))) vents.push({ x: b.x, z: b.z }); // prefer open thickets (the mother pod stays visible)
    }
    for (let k = 0; k < 400 && vents.length < C.vents; k++) { const q = polar(24, arenaR - 14); if (farFrom(q.x, q.z, vents, 30) && clearOfSolids(q.x, q.z, 2)) vents.push(q); }
    vents.forEach((v, i) => {
      const every = rng.range(SPORE.every[0], SPORE.every[1]), start = SPORE.start + i * 9 + rng.range(0, 6);
      out.push({ kind: 'fog', x: v.x, z: v.z, r: SPORE.r, start, warn: SPORE.warn, active: SPORE.active, every, power: SPORE.fog, tag: 'spore' });
      out.push({ kind: 'slow', x: v.x, z: v.z, r: SPORE.core, start, warn: SPORE.warn, active: SPORE.active, every, power: SPORE.slow, tag: 'spore-core' });
    });

    // ---- resonance geodes: open ground, clear of the crystal clusters / ruin blocks, the spawn and the spore thickets
    const geodes: P[] = [];
    for (let k = 0; k < 600 * Math.ceil(C.geodes / RESONANCE.geodes) && geodes.length < C.geodes; k++) {
      const q = polar(24, arenaR - 16);
      if (farFrom(q.x, q.z, geodes, 34) && farFrom(q.x, q.z, vents, 18) && clearOfSolids(q.x, q.z, RESONANCE.r + 2)) geodes.push(q);
    }
    geodes.forEach((g, i) => {
      out.push({ kind: 'burn', x: g.x, z: g.z, r: RESONANCE.r, start: RESONANCE.start + i * 6 + rng.range(0, 4), warn: RESONANCE.warn, active: RESONANCE.active,
        every: rng.range(RESONANCE.every[0], RESONANCE.every[1]), power: RESONANCE.power, tag: 'resonance' });
    });
    return out;
  },
};
